import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import OverlayCanvas from "./OverlayCanvas";
import useDocumentImage from "./useDocumentImage";
import overlayApi from "@serie3/mymediaplanner/Modules/Social/Domain/overlayApi";
import {
  normalizeOverlay,
  DEFAULT_CSS,
  KNOWN_FONTS,
} from "@serie3/mymediaplanner/Modules/Social/Domain/overlayRenderer";

const AUTOSAVE_DELAY = 700;

/**
 * Finds font families in the CSS that the render container does not have.
 *
 * This is the one way the published image can still differ from this preview: the
 * browser falls back to a font installed on the user's machine, the renderer falls
 * back to its own, and the text rewraps. Worth a warning rather than a surprise.
 */
const unknownFonts = (css) => {
  const match = String(css || "").match(/font-family\s*:([^;]+)/i);
  if (!match) return [];

  return match[1]
    .split(",")
    .map((name) => name.trim().replace(/^['"]|['"]$/g, ""))
    .filter(
      (name) =>
        name &&
        !/^(sans-serif|serif|monospace|cursive|fantasy|system-ui|inherit|initial)$/i.test(name) &&
        !KNOWN_FONTS.some((known) => known.toLowerCase() === name.toLowerCase())
    );
};

/**
 * Editor for the title drawn on one image of one post.
 *
 * Two controls only: drag the title where you want it, and write the CSS. The
 * image itself is never modified — the position and CSS are stored against this
 * post/image pair and only burned into a throwaway copy when the post is published.
 */
const OverlayEditorModal = ({ show, setShow, connection, title, onOverlayChange }) => {
  const { t } = useTranslation();
  const documentId = connection?.document?.id;
  const { src, size, loading, error } = useDocumentImage(documentId);

  const [overlay, setOverlay] = useState(() => normalizeOverlay(connection?.overlay));
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const skipSave = useRef(true);

  // Re-seed only when the modal opens or a different image is opened. Re-seeding on
  // every connection.overlay change reloaded this editor's own autosave echo —
  // normalised and trimmed — and overwrote what the user was typing.
  useEffect(() => {
    if (!show) return;
    skipSave.current = true;
    setOverlay(normalizeOverlay(connection?.overlay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, connection?.id]);

  /** Debounced autosave — the editor has no save button by design. */
  useEffect(() => {
    if (!show || !connection?.id) return undefined;
    if (skipSave.current) {
      skipSave.current = false;
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSaving(true);
      await overlayApi.saveOverlay(connection.id, overlay);
      setSaving(false);
      if (typeof onOverlayChange === "function") {
        onOverlayChange(connection.id, overlay);
      }
    }, AUTOSAVE_DELAY);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, show, connection?.id]);

  /**
   * What the title inherits when this image has no CSS of its own: the group's
   * default, else the post author's personal default (resolved by the API), else built-in.
   *
   * The textarea holds only this image's own CSS. Empty means "inherit" — the
   * inherited CSS is shown as placeholder, never as the value, so clearing the
   * field keeps it empty. Blank input is saved as null by normalizeOverlay.
   */
  const inheritedCss = connection?.defaultCss || DEFAULT_CSS;
  const ownCss = overlay.css ?? "";
  const inherits = ownCss.trim() === "";
  const effectiveCss = inherits ? inheritedCss : ownCss;

  const missingFonts = useMemo(() => unknownFonts(effectiveCss), [effectiveCss]);

  const download = async () => {
    if (!connection?.id || downloading) return;
    setDownloading(true);
    const name = connection.document?.original_name || "image";
    await overlayApi.downloadRendered(
      connection.id,
      overlay,
      `${name.replace(/\.[^.]+$/, "")}-title.jpg`
    );
    setDownloading(false);
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="xl"
      dialogClassName="modal-90w"
      restoreFocusOptions={{ preventScroll: true }}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">
          {t("Backend.Overlay.Title", "Title on image")}
          {saving && <Spinner animation="border" size="sm" className="ms-2" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="g-3">
          <Col lg={7}>
            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            )}
            {error && (
              <div className="alert alert-warning">
                {t("Backend.Overlay.ImageLoadFailed", "This image could not be loaded.")}
              </div>
            )}
            {!loading && !error && src && (
              <>
                <OverlayCanvas
                  overlay={overlay}
                  defaultCss={inheritedCss}
                  title={title}
                  src={src}
                  imageSize={size}
                  onChange={(box) => setOverlay((previous) => ({ ...previous, box }))}
                />
                <small className="text-muted d-block mt-2">
                  {t(
                    "Backend.Overlay.DragHint",
                    "Drag the title to move it, the square handle to change its width, arrow keys to nudge. Hold shift to disable snapping."
                  )}
                </small>
              </>
            )}
            {!loading && !error && src && !String(title || "").trim() && (
              <div className="alert alert-info mt-2 mb-0 py-2 small">
                {t(
                  "Backend.Overlay.NoTitleYet",
                  "This post has no title yet, so nothing is drawn. Type a title and it appears here."
                )}
              </div>
            )}
          </Col>

          <Col lg={5}>
            <Form.Group>
              <Form.Label className="small text-muted mb-1">
                {t("Backend.Overlay.Css", "CSS for the title")}
              </Form.Label>
              {inherits && (
                <div className="small text-muted mb-1">
                  {t(
                    "Backend.Overlay.Inherits",
                    "Empty: this image uses the group default, else the post author's personal default. Type CSS to override it for this image."
                  )}
                </div>
              )}
              <Form.Control
                as="textarea"
                rows={16}
                spellCheck={false}
                value={ownCss}
                placeholder={inheritedCss}
                onChange={(event) => {
                  const value = event.target.value;
                  // Keep the raw text while typing; blank is stored as null on save.
                  setOverlay((previous) => ({ ...previous, css: value }));
                }}
                style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
              />
              <Form.Text className="text-muted" style={{ fontSize: "0.72rem" }}>
                {t(
                  "Backend.Overlay.CssHint",
                  "Plain CSS declarations. Sizes are in pixels on a 1080px wide image, and the published picture is scaled from exactly this preview."
                )}
              </Form.Text>
            </Form.Group>

            {missingFonts.length > 0 && (
              <div className="alert alert-warning mt-2 py-2 small mb-0">
                {t(
                  "Backend.Overlay.FontWarning",
                  "{{fonts}} is not available to the renderer, so the published image may wrap the text differently. Use one of: {{known}}.",
                  { fonts: missingFonts.join(", "), known: KNOWN_FONTS.join(", ") }
                )}
              </div>
            )}

            <Button
              size="sm"
              variant="link"
              className="p-0 mt-2"
              disabled={inherits}
              onClick={() =>
                setOverlay((previous) => ({ ...previous, css: null }))
              }
            >
              {t("Backend.Overlay.ResetCss", "Clear (use the default)")}
            </Button>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          className="me-auto"
          disabled={downloading || !connection?.id}
          onClick={download}
        >
          <FontAwesomeIcon icon={downloading ? faSpinner : faDownload} spin={downloading} className="me-2" />
          {t("Backend.Overlay.Download", "Download image")}
        </Button>
        <small className="text-muted me-3" style={{ maxWidth: "360px" }}>
          {t(
            "Backend.Overlay.OriginalUntouched",
            "The original image stays untouched — the title is only burned in when the post is published."
          )}
        </small>
        <Button onClick={() => setShow(false)}>
          {t("Backend.Modal.Closebutton")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OverlayEditorModal;
